import { styled } from '@mui/material';
import {
  type DateRange,
  DateRangePicker,
  LocalizationProvider,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from '../../utils/Dayjs';

const StyledDateRangePicker = styled(DateRangePicker, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        '& .MuiPickersSectionList-root': { padding: 8 },
        '& .MuiOutlinedInput-root': { paddingRight: 0 },
        '& .MuiInputBase-root input': {
          padding: '8.5px 14px',
          paddingLeft: theme.spacing(0.5),
          display: 'flex',
          alignItems: 'center',
        },
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-1),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '.MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'hidden',
        },
        '&:hover .MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'visible',
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
      }
    : {
        '& .MuiPickersInputBase-root': { paddingLeft: 0 },
        '& .MuiPickersSectionList-root': { padding: '8.5px 14px' },
      },
);

export const DateRangeInput = ({
  values,
  onChange,
  isEditableText,
  ...rest
}: {
  values: string[];
  onChange: (value: DateRange<dayjs.Dayjs>) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const startDate = values[0] ? dayjs(values[0]) : null;
  const endDate = values[1] ? dayjs(values[1]) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledDateRangePicker
        className="fs-exclude"
        // This magic is needed to get a data-testid properly assigned
        slotProps={{
          textField: {
            inputProps: {
              'data-testid': 'fim3-custom-field-input-date-range',
              'data-dd-action-name': 'FIM 3 Custom Field - date picker redacted',
            },
          },
          popper: { disablePortal: true },
        }}
        slots={{ field: SingleInputDateRangeField }}
        calendars={2}
        value={[startDate, endDate]}
        // reduce validation noise with onAccept event instead of onChange event
        // doesn't work changing back to onChange
        // broken in roadmunk too
        onChange={onChange}
        isEditableText={isEditableText}
        open={rest.autoFocus}
        {...rest}
      />
    </LocalizationProvider>
  );
};
